import { DEFAULT_SELECT_PAGE_SIZE } from '@common/constants';
import { IUseFetchOptionsPropsBase, IUseFetchOptionsResult, SelectAsyncPaginate } from '@common/SelectAsyncPaginate';
import { Label, IOption } from '@gcl/react-core';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';
import { OptionFilter, IFilterInputBaseProps, OptionsFilter, IOptionFilter } from '../searchFilter.models';
import { getFilterValue } from '../searchFilter.services';

export interface ISelectWithSearchFilterInputProps extends IFilterInputBaseProps<OptionFilter | OptionsFilter> {}

export const SelectWithSearchFilterInput: React.FunctionComponent<ISelectWithSearchFilterInputProps> = ({
  fieldConfig,
  onChange,
  filter = {},
  disabled = false,
}) => {
  const [localValue, setLocalValue] = React.useState<IOption<any>[] | undefined>(undefined);
  const {
    fieldName,
    displayName,
    useFetchOptionsWithSearch,
    optionPageSize,
    allowMultipleSelect,
    useFetchOptionsProps,
    disable,
    selectGroupOptionByKey,
    selectDisplayFieldName,
  } = fieldConfig;

  useDeepCompareEffectNoCheck(() => {
    const value = getFilterValue(fieldName, filter);
    if (!value) {
      setLocalValue(undefined);
      return;
    }

    if (!allowMultipleSelect) {
      const optionFilter = value as IOptionFilter;
      setLocalValue([{ key: optionFilter.id, text: optionFilter.value }]);
      return;
    }

    const optionsFilter = value as IOptionFilter[];
    setLocalValue(
      optionsFilter.map((of) => {
        return { key: of.id, text: of.value };
      }),
    );
  }, [JSON.stringify(filter)]);

  const useFetchOptionsPlaceHolder = ({ keyword, pageFilter }: IUseFetchOptionsPropsBase) => {
    return useQuery(['useFetchOptionsWithSearchPlaceHolder', keyword, pageFilter?.take, pageFilter?.skip], async () => {
      return {
        options: [],
        pageSize: 0,
        pageNumber: 0,
        totalItemCount: 0,
      } as IUseFetchOptionsResult<any>;
    });
  };

  return (
    <>
      <Label labelFor={fieldName}>{displayName}</Label>
      <SelectAsyncPaginate
        displayFieldName={selectDisplayFieldName || 'text'}
        groupOptionByKey={selectGroupOptionByKey}
        multiple={!!allowMultipleSelect}
        value={localValue || undefined}
        shouldDebounce
        pageSize={optionPageSize ?? DEFAULT_SELECT_PAGE_SIZE}
        useFetchOptions={useFetchOptionsWithSearch || useFetchOptionsPlaceHolder}
        useFetchOptionsProps={useFetchOptionsProps}
        minSearchQueryLength={0}
        disabled={disabled || disable}
        onChange={(selected) => {
          if (!selected || selected.length <= 0) {
            setLocalValue(undefined);
            onChange(fieldName, undefined);
            return;
          }
          setLocalValue(selected);
          if (!allowMultipleSelect) {
            onChange(fieldName, { id: selected[0].key, value: selected[0].text.toString() });
            return;
          }

          onChange(
            fieldName,
            selected.map((s) => {
              return { id: s.key, value: s.text.toString() };
            }),
          );
        }}
        id={fieldName}
      />
    </>
  );
};
